// Zeiterfassung — Monatsübersicht mit Nachtragen und (für Personal/Geschäftsführung)
// Blick auf alle Mitarbeiter samt Arbeitszeit.
//
// Die Stempeluhr selbst sitzt in der Pille (ZeitPage); hier geht es um den Überblick:
// Was ist zusammengekommen, wo fehlt etwas, wie steht das Stundenkonto?
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import type { SteuerungsEintrag } from '../../../components/island/widgets/typen';
import {
  Briefcase, CalendarBlank, Clock as PhClock, ClockCountdown, PiggyBank, Plus as PhPlus, Rows, Scales, Target, Timer, User, UserCircle, UserGear,
} from '@phosphor-icons/react';
import { KennzahlWidget } from '../../../components/ui/KennzahlWidget';
import { Segment } from '../../../components/ui/Segment';
import { GlasAuswahl } from '../../../components/ui/GlasAuswahl';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { anteilProzent, saldoBalken, saldoTonKennzahl, saldoUnter, TON_FARBE } from '../../../lib/personalAnsicht/darstellung';
import { InfoChip, MonatSchritt, PersonalKopf, SteuerKnopf } from '../personal/teile/Rahmen';
import { AntraegeListe, TagesTabelle } from '../personal/teile/ZeitTeile';
import { ArbeitszeitDialog, LoeschAntragDialog, MehrarbeitDialog, ZeitDialog, type ZeitVorgabe } from '../personal/teile/ZeitDialoge';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { ladeModelle, ladeMitarbeiter, ladeStempel, ladeZuordnungen, stempelLoeschen } from '../../../lib/zeiterfassung/api';
import {
  anzurechnendePause, dauerMinuten, gesamtsaldo, gestempeltePause, monatsbilanz, stundenText,
  tagText, tagVon, uhrzeit, zuordnungAm,
  type Arbeitszeitmodell, type Stempel, type Zuordnung,
} from '../../../lib/zeiterfassung/berechnung';
import { wochenMinuten } from '../../../lib/zeiterfassung/arbeitszeit';
import { useZeit } from '../../../contexts/ZeitContext';
import { ladeAbwesenheiten } from '../../../lib/zeiterfassung/api';
import { type Abwesenheit } from '../../../lib/zeiterfassung/abwesenheit';
import TicketzeitAuswertung from './TicketzeitAuswertung';
import {
  korrekturEntscheiden, korrekturZurueckziehen, ladeKorrekturen,
  ladeUeberstundenAntraege, mehrarbeitEntscheiden, ladeAuszahlungen,
  type Korrektur, type UeberstundenAntrag, type Auszahlung,
} from '../../../lib/zeiterfassung/api';
import { darfPersonalVerwalten, gemerkterMitarbeiter, merkeMitarbeiter } from '../../../lib/personal/rechte';

interface Props {
  userProfile?: { id?: string; role?: string; full_name?: string } | null;
}

const monatText = (monat: string): string => {
  const [j, m] = monat.split('-').map(Number);
  return new Date(j, (m || 1) - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

const monatVerschieben = (monat: string, delta: number): string => {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(j, (m || 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ersterTag = (monat: string) => `${monat}-01`;
const letzterTag = (monat: string) => {
  const [j, m] = monat.split('-').map(Number);
  return `${monat}-${String(new Date(j, m, 0).getDate()).padStart(2, '0')}`;
};

/** Welcher Dialog offen ist — ersetzt die Formulare in der Pille und die window.prompt-Abfragen. */
type OffenerDialog =
  | { art: 'zeit'; titel: string; vorgabe?: ZeitVorgabe }
  | { art: 'arbeitszeit' }
  | { art: 'mehrarbeit' }
  | { art: 'loeschantrag'; stempelId: string; bezeichnung: string };

const ZeiterfassungSeite: React.FC<Props> = ({ userProfile }) => {
  const { notify } = useIslandActions();
  const { neuLaden: uhrNeuLaden } = useZeit();
  const { mobil } = useMobilModus();
  const { bestaetige, dialoge } = useAppMeldungen(mobil);
  const [dialog, setDialog] = useState<OffenerDialog | null>(null);
  const meineId = userProfile?.id || '';
  const darfVerwalten = darfPersonalVerwalten(userProfile);

  const [monat, setMonat] = useState(() => tagVon(new Date().toISOString()).slice(0, 7));
  const [mitarbeiterId, setMitarbeiterId] = useState(meineId);
  const [mitarbeiter, setMitarbeiter] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [modelle, setModelle] = useState<Arbeitszeitmodell[]>([]);
  const [zuordnungen, setZuordnungen] = useState<Zuordnung[]>([]);
  const [stempel, setStempel] = useState<Stempel[]>([]);
  const [alleStempel, setAlleStempel] = useState<Stempel[]>([]);
  const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [sicht, setSicht] = useState<'zeiten' | 'tickets' | 'antraege'>('zeiten');
  const [korrekturen, setKorrekturen] = useState<Korrektur[]>([]);
  const [mehrarbeit, setMehrarbeit] = useState<UeberstundenAntrag[]>([]);
  const [auszahlungen, setAuszahlungen] = useState<Auszahlung[]>([]);

  useEffect(() => { setMitarbeiterId(meineId); }, [meineId]);
  const waehleMitarbeiter = (id: string) => { setMitarbeiterId(id); merkeMitarbeiter(id); };

  useEffect(() => {
    if (!darfVerwalten) return;
    void ladeMitarbeiter().then((liste) => {
      setMitarbeiter(liste);
      // Wer selbst nicht zur Belegschaft zählt (Entwickler-/Adminzugang), landet
      // sonst auf einer leeren eigenen Akte — dann den ersten Eintrag zeigen.
      // #548: zuletzt gewählte Person bleibt beim Wechsel Zeiterfassung ↔ Personalakte.
      const gemerkt = gemerkterMitarbeiter();
      if (gemerkt && liste.some((m) => m.id === gemerkt)) setMitarbeiterId(gemerkt);
      else if (liste.length > 0 && !liste.some((m) => m.id === meineId)) setMitarbeiterId(liste[0].id);
    });
  }, [darfVerwalten]);

  const laden = useCallback(async () => {
    if (!mitarbeiterId) return;
    setLaedt(true);
    const bisHeute = tagVon(new Date().toISOString());
    const [m, z, monatsStempel, saldoStempel, abw] = await Promise.all([
      ladeModelle(),
      ladeZuordnungen(mitarbeiterId),
      ladeStempel(mitarbeiterId, ersterTag(monat), letzterTag(monat)),
      // Für den Gesamtsaldo alle Zeiten ab der ersten Zuordnung — ohne sie fehlt der Übertrag.
      ladeStempel(mitarbeiterId, '2020-01-01', bisHeute),
      ladeAbwesenheiten(mitarbeiterId),
    ]);
    setAbwesenheiten(abw);
    // Personal/GF sieht ALLE offenen Anträge, Mitarbeitende die eigenen.
    setKorrekturen(await ladeKorrekturen(darfVerwalten ? null : mitarbeiterId));
    setMehrarbeit(await ladeUeberstundenAntraege(darfVerwalten ? null : mitarbeiterId));
    setAuszahlungen(await ladeAuszahlungen(mitarbeiterId));
    setModelle(m);
    setZuordnungen(z);
    setStempel(monatsStempel);
    setAlleStempel(saldoStempel);
    setLaedt(false);
  }, [mitarbeiterId, monat, darfVerwalten]);

  useEffect(() => { void laden(); }, [laden]);

  const bilanz = useMemo(
    () => monatsbilanz(monat, stempel, zuordnungen, modelle, new Date(), abwesenheiten),
    [monat, stempel, zuordnungen, modelle, abwesenheiten],
  );
  const gesamt = useMemo(
    () => gesamtsaldo(
      alleStempel, zuordnungen, modelle, tagVon(new Date().toISOString()), new Date(), abwesenheiten,
      auszahlungen.map((a) => ({ datum: a.datum, minuten: a.minuten })),
    ),
    [alleStempel, zuordnungen, modelle, abwesenheiten, auszahlungen],
  );
  // Seit persönliche Zeiten die Vorlage schlagen, wäre der bloße Vorlagenname irreführend
  // („Teilzeit 20 Std." bei tatsächlich 22). Darum die echte Wochenarbeitszeit zeigen.
  const modellName = useMemo(() => {
    const z = [...zuordnungen].reverse().find((x) => x.gueltig_ab <= letzterTag(monat));
    if (!z) return 'keine Arbeitszeit hinterlegt';
    if (z.nach_stunden) return 'Abrechnung nach Stunden';
    const eigene = z.soll_minuten;
    const woche = wochenMinuten(eigene ?? modelle.find((m) => m.id === z.modell_id)?.soll_minuten);
    const vorlage = modelle.find((m) => m.id === z.modell_id)?.name;
    if (eigene) return `${stundenText(woche)}/Woche`;
    return vorlage ? `${vorlage} · ${stundenText(woche)}/Woche` : `${stundenText(woche)}/Woche`;
  }, [zuordnungen, modelle, monat]);

  const offeneKorrekturen = useMemo(
    () => korrekturen.filter((k) => k.status === 'offen').length + mehrarbeit.filter((m) => m.status === 'offen').length,
    [korrekturen, mehrarbeit],
  );

  const mehrarbeitEntscheidung = async (m: UeberstundenAntrag, status: 'genehmigt' | 'abgelehnt') => {
    const fehler = await mehrarbeitEntscheiden(m.id, status);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    notify({ title: status === 'genehmigt' ? 'Mehrarbeit genehmigt' : 'Mehrarbeit abgelehnt', tone: 'success' });
    void laden();
  };

  const mehrarbeitAntrag = () => setDialog({ art: 'mehrarbeit' });

  const entscheiden = async (k: Korrektur, status: 'genehmigt' | 'abgelehnt') => {
    const fehler = await korrekturEntscheiden(k.id, status);
    if (fehler) { notify({ title: fehler, tone: 'error' }); return; }
    notify({ title: status === 'genehmigt' ? 'Korrektur übernommen' : 'Antrag abgelehnt', tone: 'success' });
    void laden();
    void uhrNeuLaden();
  };

  const nachFormular = () => {
    setDialog(null);
    void laden();
    void uhrNeuLaden();
  };

  const neuerEintrag = (tag?: string) => {
    setDialog({
      art: 'zeit',
      titel: darfVerwalten ? 'Zeit nachtragen' : 'Korrektur beantragen',
      vorgabe: tag ? { tag, von: '08:00', bis: '17:00', pause: 30, notiz: '' } : undefined,
    });
  };

  const bearbeiten = (s: Stempel) => {
    const brutto = dauerMinuten(s.beginn, s.ende);
    setDialog({
      art: 'zeit',
      titel: darfVerwalten ? 'Zeit korrigieren' : 'Korrektur beantragen',
      vorgabe: {
        id: s.id,
        tag: tagVon(s.beginn),
        von: uhrzeit(s.beginn),
        bis: uhrzeit(s.ende) || uhrzeit(new Date().toISOString()),
        pause: anzurechnendePause(brutto, gestempeltePause(s)),
        notiz: s.notiz || '',
      },
    });
  };

  const arbeitszeitOeffnen = () => setDialog({ art: 'arbeitszeit' });

  const loeschen = async (s: Stempel) => {
    const bezeichnung = `${tagText(tagVon(s.beginn))} (${uhrzeit(s.beginn)}–${uhrzeit(s.ende)})`;
    if (darfVerwalten) {
      if (!await bestaetige({ nachricht: `Buchung vom ${bezeichnung} wirklich löschen?`, label: 'Löschen' })) return;
      const ok = await stempelLoeschen(s.id);
      if (ok) { notify({ title: 'Buchung gelöscht', tone: 'success' }); void laden(); void uhrNeuLaden(); }
      return;
    }
    // Mitarbeitende beantragen die Löschung — entschieden wird in der Personalabteilung.
    setDialog({ art: 'loeschantrag', stempelId: s.id, bezeichnung });
  };

  usePillenSteuerung({
    titel: 'Zeiterfassung',
    eintraege: [
      { id: 'monat', typ: 'schritt', farbe: 'indigo', icon: CalendarBlank, kicker: 'Monat', titel: monatText(monat), voll: true,
        onZurueck: () => setMonat(monatVerschieben(monat, -1)), onVor: () => setMonat(monatVerschieben(monat, 1)),
        zurueckLabel: 'Vorheriger Monat', vorLabel: 'Nächster Monat' },
      ...(darfVerwalten
        ? [{ id: 'mitarbeiter', typ: 'auswahl', farbe: 'violett', icon: UserCircle, kicker: 'Mitarbeiter', wert: mitarbeiterId, onChange: waehleMitarbeiter,
            unter: 'Zeiten ansehen und verwalten',
            optionen: mitarbeiter.map(m => ({ wert: m.id, label: m.full_name || m.email || '' })) } satisfies SteuerungsEintrag]
        : []),
      { id: 'sicht', typ: 'segment', farbe: 'blau', icon: Rows, kicker: 'Ansicht', titel: 'Sicht', voll: true, wert: sicht,
        onChange: (w: string) => setSicht(w as typeof sicht),
        optionen: [
          { wert: 'zeiten', label: 'Arbeitszeit' }, { wert: 'tickets', label: 'Ticketzeiten' },
          { wert: 'antraege', label: `Anträge${offeneKorrekturen > 0 ? ` (${offeneKorrekturen})` : ''}` },
        ] },
      { id: 'nachtragen', typ: 'aktion', farbe: 'gold', icon: PhPlus, kicker: 'Anlegen', titel: darfVerwalten ? 'Zeit nachtragen' : 'Korrektur beantragen',
        unter: 'Vergessene Zeiten erfassen', onClick: () => neuerEintrag() },
      { id: 'mehrarbeit', typ: 'aktion', farbe: 'pink', icon: PhClock, kicker: 'Antrag', titel: 'Mehrarbeit beantragen', unter: 'Überstunden anfragen',
        onClick: mehrarbeitAntrag },
      ...(darfVerwalten
        ? [{ id: 'arbeitszeit', typ: 'aktion', farbe: 'indigo', icon: UserGear, kicker: 'Einstellungen', titel: 'Arbeitszeit', unter: 'Sollzeiten festlegen',
            onClick: arbeitszeitOeffnen } satisfies SteuerungsEintrag]
        : []),
    ],
  }, [monat, mitarbeiterId, mitarbeiter, modelle, zuordnungen, darfVerwalten, sicht, offeneKorrekturen]);

  const heute = tagVon(new Date().toISOString());
  const nameVon = (id: string) => mitarbeiter.find((m) => m.id === id)?.full_name || '—';
  const istAnteil = anteilProzent(bilanz.istMinuten, bilanz.sollMinuten);
  // Abrechnung nach Stunden (#536): „% vom Soll“ wäre immer 100 und sagt nichts.
  const nachStunden = !!zuordnungAm(zuordnungen, letzterTag(monat))?.nach_stunden;
  const saldoTon = TON_FARBE[saldoTonKennzahl(bilanz.saldoMinuten)];
  const kontoTon = TON_FARBE[saldoTonKennzahl(gesamt)];

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PersonalKopf bereich="zeiterfassung" verwalten={darfVerwalten} mobil={mobil} />

      {/* Steuerzeile — dieselben Handler wie die Pille */}
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <MonatSchritt text={monatText(monat)} mobil={mobil}
          onZurueck={() => setMonat(monatVerschieben(monat, -1))} onVor={() => setMonat(monatVerschieben(monat, 1))} />
        {darfVerwalten && (
          <GlasAuswahl wert={mitarbeiterId} onChange={waehleMitarbeiter} icon={User} ariaLabel="Mitarbeiter" tip="Mitarbeiter – Zeiten dieser Person anzeigen"
            style={{ maxWidth: '100%' }}
            optionen={mitarbeiter.map((m) => ({ wert: m.id, label: m.full_name || m.email || '' }))} />
        )}
        <Segment<'zeiten' | 'tickets' | 'antraege'> ariaLabel="Sicht" hoehe={mobil ? 44 : 34} wert={sicht} onChange={setSicht}
          eintraege={[
            { key: 'zeiten', label: 'Arbeitszeit', tip: 'Arbeitszeit – Tageweise Buchungen und Saldo' },
            { key: 'tickets', label: 'Ticketzeiten', tip: 'Ticketzeiten – Auf Tickets gebuchte Zeit nach Objekt, Eigentümer und Ticket' },
            { key: 'antraege', label: `Anträge${offeneKorrekturen > 0 ? ` (${offeneKorrekturen})` : ''}`, tip: 'Anträge – Korrekturen und Mehrarbeit' },
          ]} />
        <div className="flex flex-wrap" style={{ gap: 8, marginLeft: mobil ? 0 : 'auto' }}>
          <SteuerKnopf icon={ClockCountdown} onClick={mehrarbeitAntrag} tip="Mehrarbeit beantragen – Geplante Mehrarbeit vorab genehmigen lassen">Mehrarbeit beantragen</SteuerKnopf>
          {darfVerwalten && (
            <SteuerKnopf icon={UserGear} tip="Arbeitszeit – Wochenstunden, Urlaubstage und Startsaldo dieser Person festlegen"
              onClick={arbeitszeitOeffnen}>Arbeitszeit</SteuerKnopf>
          )}
          <SteuerKnopf icon={PhPlus} primaer onClick={() => neuerEintrag()}
            tip={darfVerwalten ? 'Zeit nachtragen – Fehlende Arbeitszeit direkt erfassen' : 'Zeit nachtragen – Als Korrekturantrag an die Personalabteilung'}>
            Zeit nachtragen
          </SteuerKnopf>
        </div>
      </div>

      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <InfoChip icon={Briefcase} tip="Arbeitszeit – Gültige Wochenarbeitszeit dieser Person">{modellName}</InfoChip>
        {darfVerwalten && mitarbeiterId !== meineId && (
          <InfoChip icon={User} farbe="var(--info)" flaeche="var(--info-soft)" tip="Ansicht – Sie sehen die Zeiten dieser Person">
            {mitarbeiter.find((m) => m.id === mitarbeiterId)?.full_name || 'Mitarbeiter'}
          </InfoChip>
        )}
      </div>

      <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' }}>
        <KennzahlWidget label="Ist" wert={stundenText(bilanz.istMinuten)} unter={nachStunden ? 'wird nach Stunden bezahlt' : `${istAnteil}% vom Soll`} icon={Timer} farbe="var(--acc)"
          balken={{ anteil: istAnteil, farbe: 'var(--acc-fill)' }} tip="Ist – Gearbeitete Zeit im Monat inkl. anzurechnender Abwesenheiten" />
        <KennzahlWidget label="Soll" wert={stundenText(bilanz.sollMinuten)} unter="bis heute" icon={Target} farbe="var(--info)"
          balken={{ anteil: 100, farbe: 'var(--info)' }} tip="Soll – Vereinbarte Arbeitszeit bis heute" />
        <KennzahlWidget label="Saldo Monat" wert={stundenText(bilanz.saldoMinuten, true)} unter={saldoUnter(bilanz.saldoMinuten)} icon={Scales}
          farbe={saldoTon.farbe} wertFarbe={saldoTon.farbe} balken={{ anteil: saldoBalken(bilanz.saldoMinuten, bilanz.sollMinuten), farbe: saldoTon.farbe }}
          tip="Saldo Monat – Ist minus Soll in diesem Monat" />
        {/* Ohne Balken (Entscheidung 18.10.2026): fürs Gesamtkonto gibt es keine Bezugsgröße. */}
        <KennzahlWidget label="Stundenkonto gesamt" wert={stundenText(gesamt, true)} unter="inkl. Startsaldo, nach Auszahlungen" icon={PiggyBank}
          farbe={kontoTon.farbe} wertFarbe={kontoTon.farbe}
          tip="Stundenkonto gesamt – Übertrag plus alle Monate, abzüglich ausgezahlter Stunden" />
      </div>

      {sicht === 'antraege' ? (
        <AntraegeListe mehrarbeit={mehrarbeit} korrekturen={korrekturen} verwalten={darfVerwalten} meineId={meineId} name={nameVon} mobil={mobil}
          onMehrarbeit={(m, st) => { void mehrarbeitEntscheidung(m, st); }}
          onKorrektur={(k, st) => { void entscheiden(k, st); }}
          onZurueckziehen={async (k) => { await korrekturZurueckziehen(k.id); void laden(); }} />
      ) : sicht === 'tickets' ? (
        <TicketzeitAuswertung
          vonTag={ersterTag(monat)}
          bisTag={letzterTag(monat)}
          mitarbeiterId={mitarbeiterId}
          alleMitarbeiter={darfVerwalten && mitarbeiterId === meineId}
          namen={Object.fromEntries(mitarbeiter.map((m) => [m.id, m.full_name || m.email || '']))}
        />
      ) : (
        <TagesTabelle zeilen={bilanz.zeilen} heute={heute} mobil={mobil} laedt={laedt}
          onNachtragen={(tag) => neuerEintrag(tag)} onBearbeiten={bearbeiten} onLoeschen={(st) => { void loeschen(st); }} />
      )}
      {dialog?.art === 'zeit' && (
        <ZeitDialog titel={dialog.titel} mitarbeiterId={mitarbeiterId} vorgabe={dialog.vorgabe} direkt={darfVerwalten}
          onFertig={nachFormular} onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'arbeitszeit' && (
        <ArbeitszeitDialog mitarbeiterId={mitarbeiterId} modelle={modelle} vorhanden={zuordnungen}
          onFertig={nachFormular} onAbbrechen={() => setDialog(null)} />
      )}
      {dialog?.art === 'mehrarbeit' && (
        <MehrarbeitDialog mitarbeiterId={mitarbeiterId} onAbbrechen={() => setDialog(null)}
          onFertig={() => { setDialog(null); notify({ title: 'Mehrarbeit beantragt', tone: 'success' }); void laden(); }} />
      )}
      {dialog?.art === 'loeschantrag' && (
        <LoeschAntragDialog mitarbeiterId={mitarbeiterId} stempelId={dialog.stempelId} bezeichnung={dialog.bezeichnung} onAbbrechen={() => setDialog(null)}
          onFertig={() => { setDialog(null); notify({ title: 'Löschung beantragt', tone: 'success' }); void laden(); }} />
      )}
      {dialoge}
      <GlasTooltipSchicht />
    </div>
  );
};

export default ZeiterfassungSeite;
